/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box, List, ListItem, ListItemText, Typography, useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAppSelector } from '../../store/hooks';
import { selectTopTopics, selectGetTopTopicsState } from '../../store/slices/contentSlice';
import { TopicsCount } from '../../common/models/content/TopicsCount';
import { getSortedTopTopics, ITopic } from './Content/ContentCommonFunctions';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';

export function TopicsCard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { trackEngagement } = useCustomEventTracking();
  // commenting this if we need scroll back to the selected topic after navigation
  // const navigationSelectedIndex = location?.state?.selectedIndex;
  // const navigationSelectedAllTopicsIndex = location?.state?.selectedAllTopicsIndex;
  const navigationShowAllTopicsState = location?.state?.showAllTopicsState;
  const [showAllTopics, setShowAllTopics] = useState(navigationShowAllTopicsState || false);
  const getAllTopicsState = useAppSelector(selectGetTopTopicsState);
  const allTopics = useAppSelector(selectTopTopics);
  const sortedTopTopics =
    useMemo(() => (getSortedTopTopics(allTopics, true)), [allTopics]);
  const [topicsToShow, setTopicsToShow] = useState(sortedTopTopics);

  // commenting this if we need scroll back to the selected topic after navigation
  // useEffect(() => {
  //   if (typeof navigationSelectedIndex === 'number' && !Number.isNaN(navigationSelectedIndex)) {
  //     setSelectedIndex(navigationSelectedIndex);
  //   }
  //   if (typeof navigationSelectedAllTopicsIndex ===
  // 'number' && !Number.isNaN(navigationSelectedAllTopicsIndex)) {
  //     setSelectedAllTopicsIndex(navigationSelectedAllTopicsIndex);
  //   }
  // }, [navigationSelectedIndex, navigationSelectedAllTopicsIndex]);

  useEffect(() => {
    if (showAllTopics) {
      setTopicsToShow(sortedTopTopics);
    } else {
      setTopicsToShow(sortedTopTopics.slice(0, 5));
    }
  }, [showAllTopics, sortedTopTopics]);

  const handleAllTopicClick = (topic: ITopic | TopicsCount, index: number) => {
    const params = new URLSearchParams();
    if (('category' in topic) && topic.category === 'Business Goal') {
      params.append('businessGoal', topic.name || '');
    } else {
      params.append('topic', topic.name || '');
    }
    const route = `/search?${params.toString()}`;
    navigate(route, {
      state: {
        selectedAllTopicsIndex: index,
        selectedIndex: -1,
      },
    });
    // fire an event with topic name
    trackEngagement(
      'DiscoverMoreTopicsCard',
      'Select',
      'Topic',
      {
        topic,
      },
    );
  };

  return (
    <Box
      sx={{
        paddingBottom: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        width: '100%',
        height: 'fit-content',
      }}
    >
      <Box
        sx={{
          cursor: 'default',
          marginBottom: theme.spacing(2),
        }}
      >
        <Typography variant='h7'>{t('dashboard.spotlightTopics.heading')}</Typography>
      </Box>
      <Box sx={{
        background: theme.palette.brand.surface,
        borderRadius: theme.spacing(2),
      }}>
        {getAllTopicsState === 'pending' ? null :
          <Box>
            <List
              sx={{
                display: 'flex',
                width: 'fit-content',
                flexDirection: 'row',
                flexWrap: 'wrap',
                padding: theme.spacing(1),
              }}
            >
              {topicsToShow.map((topic, index) => (
                <ListItem
                  key={index}
                  onClick={() => {
                    handleAllTopicClick(topic, index);
                    window.scrollTo(0, 0);
                  }}
                  sx={{
                    display: 'inline',
                    width: 'fit-content',
                    padding: 0,
                    mr: theme.spacing(1),
                  }}
                >
                  <ListItemText
                    sx={{
                      flex: '0 1 auto',
                      padding: theme.spacing(1),
                      borderRadius: theme.spacing(1),
                      backgroundColor: theme.palette.common.white,
                      color: theme.palette.grey[500],
                      border: `1px solid ${theme.palette.grey[200]}`,
                    }}
                    primary={topic.name} />
                </ListItem>
              ))}
            </List>
          </Box>
        }
        <Box
          sx={{
            color: theme.palette.brand.primary,
            display: 'flex',
            justifyContent: 'center',
            paddingBottom: theme.spacing(2),
          }}
        >
          <Typography variant='body1'
            onClick={() => setShowAllTopics(!showAllTopics)}
          > {showAllTopics ? t('dashboard.spotlightTopics.showLess') : t('dashboard.spotlightTopics.showMore')}
          </Typography>
          {
            showAllTopics ?
              <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
          }
        </Box>
      </Box>
    </Box >
  );
}
